import React, { useState, useEffect, useRef } from "react";
import { apiCall } from "../../helpers/api";

export default function PredictiveBids({ categories, followerCount, engagementRate }) {
    const [prediction, setPrediction] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestId = useRef(0);

    const hasRequiredInputs = categories?.length > 0 && followerCount > 0;

    useEffect(() => {
        if (!hasRequiredInputs) {
            // Invalidate any in-flight requests so stale responses can't update state
            requestId.current += 1;
            setPrediction(null);
            setLoading(false);
            setError(null);
            return;
        }
        
        setLoading(true);

        requestId.current += 1;
        const currentRequest = requestId.current;

        const timer = setTimeout(async () => {
            setError(null);
            try {
                const encodedCategories = encodeURIComponent(JSON.stringify(categories));
                const response = await apiCall(
                    `prediction?categories=${encodedCategories}&followerCount=${followerCount}&engagementRate=${engagementRate}`,
                    { method: "GET" }
                );
                if (currentRequest === requestId.current) {
                    if (response["success"]) {
                        setPrediction(response["success"]["prediction"]);
                        setError(null);
                    } else {
                        setPrediction(null);
                        setError("Unable to calculate predicted bids right now.");
                    }
                    setLoading(false);
                }
            } catch {
                if (currentRequest === requestId.current) {
                    setPrediction(null);
                    setError("Unable to calculate predicted bids right now.");
                    setLoading(false);
                }
            }
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, [categories, followerCount, engagementRate, hasRequiredInputs]);


    if (!hasRequiredInputs) {
        return (
            <div className="rounded-xl border bg-white p-3 mt-3 d-flex align-items-center">
                <span className="me-2" style={{display: 'flex', alignItems: 'center'}}>
                    {/* Info icon SVG */}
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <circle cx="10" cy="10" r="9" stroke="#0d6efd" strokeWidth="2" fill="#e9f5ff" />
                        <rect x="9" y="8" width="2" height="6" rx="1" fill="#0d6efd" />
                        <rect x="9" y="5" width="2" height="2" rx="1" fill="#0d6efd" />
                    </svg>
                </span>
                <span className="small">
                    <strong>Select a category</strong> and set minimum followers and engagement rate to see predicted bids.
                </span>
            </div>
        );
    }


    if (loading) {
        return (
            <div className="rounded-xl border bg-white p-3 mt-3 d-flex align-items-center">
                <span className="text-muted small">Calculating predicted bids...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-xl border bg-white p-3 mt-3 d-flex align-items-center">
                <span className="me-2 text-danger" style={{display: 'flex', alignItems: 'center'}}>
                    {/* Warning icon SVG */}
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <circle cx="10" cy="10" r="9" stroke="#dc3545" strokeWidth="2" fill="#f8d7da" />
                        <rect x="9" y="7" width="2" height="6" rx="1" fill="#dc3545" />
                        <rect x="9" y="14" width="2" height="2" rx="1" fill="#dc3545" />
                    </svg>
                </span>
                <span className="small text-danger">{error}</span>
            </div>
        );
    }

    if (prediction === null) {
        return null;
    }

    return (
        <div className="rounded-xl border bg-white p-3 mt-3 d-flex align-items-center">
            <span className="me-2 text-success" style={{display: 'flex', alignItems: 'center'}}>
                {/* Checkmark icon SVG */}
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="9" stroke="#198754" strokeWidth="2" fill="#e6f4ea" />
                    <path d="M6 11l3 3 5-6" stroke="#198754" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
            </span>
            <span className="fw-bold">Estimated bids: ~{prediction}</span>
        </div>
    );
}